import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, Send, Loader2, Link2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminReviewNudge = () => {
  const [reviewUrl, setReviewUrl] = useState("");
  const [savedUrl, setSavedUrl] = useState("");
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(null);
  const [rating, setRating] = useState("");
  const [count, setCount] = useState("");

  useEffect(() => {
    axios.get(`${API}/admin/settings`, { withCredentials: true })
      .then((r) => {
        setReviewUrl(r.data.google_review_url || "");
        setSavedUrl(r.data.google_review_url || "");
        setRating(r.data.google_rating ?? "");
        setCount(r.data.google_review_count ?? "");
      })
      .catch(() => {});
    axios.get(`${API}/admin/customers`, { withCredentials: true })
      .then((r) => setCustomers(r.data.customers))
      .catch(() => {});
  }, []);

  const saveUrl = async () => {
    setBusy("save");
    try {
      await axios.post(`${API}/admin/settings`, {
        google_review_url: reviewUrl,
        google_rating: rating === "" ? null : parseFloat(rating),
        google_review_count: count === "" ? null : parseInt(count, 10),
      }, { withCredentials: true });
      setSavedUrl(reviewUrl);
      toast.success("Saved — the homepage badge updates instantly.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save.");
    } finally {
      setBusy(null);
    }
  };

  const sendNudge = async () => {
    setBusy("send");
    try {
      await axios.post(`${API}/admin/review-nudge`, { customer_email: selected }, { withCredentials: true });
      toast.success(`Review request sent to ${selected}.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send the review request.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="rounded-3xl border border-white/15 bg-[#0F1B31]/80 p-8" data-testid="admin-review-nudge">
      <p className="font-mono-label text-xs uppercase tracking-[0.3em] text-slate-400 mb-6 flex items-center gap-2">
        <Star className="h-4 w-4" /> Review Request Nudge
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="url" value={reviewUrl} onChange={(e) => setReviewUrl(e.target.value)}
          placeholder="Paste your Google review link (https://...)" data-testid="review-url-input"
          className="flex-1 rounded-xl border border-white/15 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-white/40"
        />
        <button
          onClick={saveUrl} disabled={busy === "save" || !reviewUrl.trim()} data-testid="review-url-save-button"
          className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm font-semibold text-slate-200 transition-colors duration-300 hover:bg-white hover:text-[#060D1A] disabled:opacity-50"
        >
          {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
          Save Link
        </button>
      </div>
      <div className="mt-4 flex flex-col sm:flex-row gap-3">
        <input
          type="number" step="0.1" min="0" max="5" value={rating} onChange={(e) => setRating(e.target.value)}
          placeholder="Google rating (e.g. 4.9)" data-testid="google-rating-input"
          className="flex-1 rounded-xl border border-white/15 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-white/40"
        />
        <input
          type="number" min="0" value={count} onChange={(e) => setCount(e.target.value)}
          placeholder="Review count (e.g. 12)" data-testid="google-review-count-input"
          className="flex-1 rounded-xl border border-white/15 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-white/40"
        />
      </div>
      <p className="mt-2 text-xs text-slate-600">Rating + count power the homepage badge. Leave blank to keep the badge hidden.</p>
      <div className="mt-4 flex flex-col sm:flex-row gap-3">
        <select
          value={selected} onChange={(e) => setSelected(e.target.value)} data-testid="review-customer-select"
          className="flex-1 rounded-xl border border-white/15 bg-[#0B162B] px-4 py-3 text-sm text-white outline-none focus:border-white/40"
        >
          <option value="">Choose a customer…</option>
          {customers.map((c) => (
            <option key={c.email} value={c.email}>{c.name ? `${c.name} — ` : ""}{c.email}</option>
          ))}
        </select>
        <button
          onClick={sendNudge} disabled={busy === "send" || !selected} data-testid="review-nudge-send-button"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-[#060D1A] transition-colors duration-300 hover:bg-slate-300 disabled:opacity-50"
        >
          {busy === "send" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Send Review Request
        </button>
      </div>
      {savedUrl && (
        <p className="mt-3 text-xs text-slate-500" data-testid="review-url-status">Link saved — nudges include a one-tap review button.</p>
      )}
    </div>
  );
};
